import {useCallback, useSyncExternalStore} from 'react'

const noopSubscribe = () => () => {}

/**
 * Subscribes to a CSS media query and re-renders when it flips.
 *
 * useSyncExternalStore (rather than useState + effect) so the first paint
 * already reflects the real viewport — an effect-based hook renders the wrong
 * branch once, which for the grid means mounting a full table only to throw it
 * away for cards a tick later.
 */
export function useMediaQuery(query: string): boolean {
  const supported = typeof window !== 'undefined' && typeof window.matchMedia === 'function'

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (!supported) return () => {}
      const mql = window.matchMedia(query)
      mql.addEventListener('change', onStoreChange)
      return () => mql.removeEventListener('change', onStoreChange)
    },
    [query, supported]
  )

  const getSnapshot = useCallback(
    () => (supported ? window.matchMedia(query).matches : false),
    [query, supported]
  )

  return useSyncExternalStore(supported ? subscribe : noopSubscribe, getSnapshot, () => false)
}

export default useMediaQuery
